/**
 * advisor-facts.js — the plan advisor's fact-find (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md).
 *
 * Pure: schema, sanitising, the required-fact check, and the mapping to and from the intake draft.
 * No DOM. The fact-find panel (advisor-ui.js) edits a facts object through these helpers; the
 * engine (advisor-engine.js) reads it.
 *
 * Household, ZIP, effective date, income and tobacco stay on the intake form — buildFactsFromDraft
 * folds them in, so they are never asked twice.
 */

export const HEALTH_LEVELS = ['none', 'minor', 'significant'];
export const USAGE_LEVELS = ['low', 'moderate', 'high'];
export const PRIORITIES = ['lowest_price', 'best_coverage', 'keep_doctors', 'balanced'];
export const NETWORK_WANTS = ['any', 'hmo', 'epo', 'ppo'];
export const ADD_ONS = ['dental', 'vision', 'accident', 'criticalIllness'];
export const CONDITIONS = [
  'diabetes', 'heart_disease', 'cancer_history', 'asthma_copd', 'mental_health', 'joint_back', 'other',
];
export const MEMBER_ROLES = ['primary', 'spouse', 'child'];

// Children are not asked about pregnancy; every adult is.
const ADULT_ROLES = ['primary', 'spouse'];
const MAX_MEMBERS = 12;
const MAX_LIST = 25;
const MAX_TEXT = 120;

function oneOf(value, allowed) {
  return allowed.includes(value) ? value : null;
}

function bool(value) {
  if (value === true || value === false) return value;
  return null;
}

function text(value) {
  if (value == null) return '';
  return String(value).replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, MAX_TEXT);
}

function age(value) {
  const n = Number(value);
  if (value === null || value === '' || !Number.isFinite(n) || n < 0 || n > 120) return null;
  return Math.floor(n);
}

function money(value) {
  const n = Number(value);
  if (value === null || value === '' || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(Math.min(n, 100000) * 100) / 100;
}

/** A blank facts object for a household of the given members. */
export function createFacts(members = [{ role: 'primary', age: null }]) {
  return sanitizeFacts({ members });
}

function sanitizeMember(m, index) {
  const src = m && typeof m === 'object' ? m : {};
  const role = oneOf(src.role, MEMBER_ROLES) || (index === 0 ? 'primary' : 'child');
  const conditions = Array.isArray(src.conditions)
    ? [...new Set(src.conditions.filter((c) => CONDITIONS.includes(c)))]
    : [];
  return {
    role,
    age: age(src.age),
    health: oneOf(src.health, HEALTH_LEVELS),
    conditions,
    plannedProcedure: bool(src.plannedProcedure),
    pregnant: ADULT_ROLES.includes(role) ? bool(src.pregnant) : false,
  };
}

function sanitizeDoctor(d) {
  const npi = String((d && d.npi) || '').replace(/\D/g, '');
  if (npi.length !== 10) return null;
  return { npi, name: text(d.name) };
}

function sanitizeDrug(d) {
  const rxcui = String((d && d.rxcui) || '').replace(/\D/g, '');
  if (!rxcui || rxcui.length > 10) return null;
  return { rxcui, name: text(d.name) };
}

/**
 * Return a clean copy of any facts-shaped input. Unknown keys are dropped; invalid values become
 * null (unanswered), never a guess.
 */
export function sanitizeFacts(input) {
  const src = input && typeof input === 'object' ? input : {};
  const members = (Array.isArray(src.members) ? src.members : [])
    .slice(0, MAX_MEMBERS)
    .map(sanitizeMember);
  if (members.length === 0) members.push(sanitizeMember({ role: 'primary' }, 0));
  const wants = src.wants && typeof src.wants === 'object' ? src.wants : {};
  const addOnsIn = src.addOns && typeof src.addOns === 'object' ? src.addOns : {};
  const addOns = {};
  ADD_ONS.forEach((k) => { addOns[k] = addOnsIn[k] === true; });
  const dedupe = (list, key) => {
    const seen = new Set();
    return list.filter((x) => x && !seen.has(x[key]) && seen.add(x[key]));
  };
  return {
    budgetMonthly: money(src.budgetMonthly),
    members,
    usage: oneOf(src.usage, USAGE_LEVELS),
    priority: oneOf(src.priority, PRIORITIES),
    doctors: dedupe((Array.isArray(src.doctors) ? src.doctors : []).slice(0, MAX_LIST).map(sanitizeDoctor), 'npi'),
    drugs: dedupe((Array.isArray(src.drugs) ? src.drugs : []).slice(0, MAX_LIST).map(sanitizeDrug), 'rxcui'),
    wants: {
      hsa: bool(wants.hsa),
      networkType: oneOf(wants.networkType, NETWORK_WANTS),
    },
    addOns,
    state: /^[A-Z]{2}$/.test(String(src.state || '')) ? String(src.state) : null,
    zip: /^\d{5}$/.test(String(src.zip || '')) ? String(src.zip) : null,
    tobacco: bool(src.tobacco),
  };
}

/**
 * The required facts that are still unanswered, as stable codes the UI can label.
 * An empty list means the suggestion can be firm; otherwise it is provisional.
 */
export function missingFacts(facts) {
  const f = sanitizeFacts(facts);
  const missing = [];
  if (f.budgetMonthly == null) missing.push('budget');
  if (f.usage == null) missing.push('usage');
  if (f.priority == null) missing.push('priority');
  f.members.forEach((m, i) => {
    if (m.health == null) missing.push(`member${i}.health`);
    if (m.plannedProcedure == null) missing.push(`member${i}.plannedProcedure`);
    if (ADULT_ROLES.includes(m.role) && m.pregnant == null) missing.push(`member${i}.pregnant`);
  });
  return missing;
}

/** True when any covered person has significant needs, a planned procedure, or a pregnancy. */
export function hasHighHealthNeed(facts) {
  return sanitizeFacts(facts).members.some(
    (m) => m.health === 'significant' || m.plannedProcedure === true || m.pregnant === true,
  );
}

/** The worst health level across the household ('none' when nobody has answered). */
export function householdHealth(facts) {
  const levels = sanitizeFacts(facts).members.map((m) => m.health);
  if (levels.includes('significant')) return 'significant';
  if (levels.includes('minor')) return 'minor';
  return 'none';
}

function smoker(v, fallback) {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return fallback;
}

function ageFromDob(dob, asOf) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dob || ''));
  if (!m) return null;
  const ref = asOf instanceof Date ? asOf : new Date();
  let years = ref.getUTCFullYear() - Number(m[1]);
  const beforeBirthday = (ref.getUTCMonth() + 1) < Number(m[2])
    || ((ref.getUTCMonth() + 1) === Number(m[2]) && ref.getUTCDate() < Number(m[3]));
  if (beforeBirthday) years -= 1;
  return years >= 0 ? years : null;
}

/**
 * Fold the intake draft (household, ZIP, tobacco) into a facts object, keeping every answer the
 * agent already gave in `previous`. Members line up by role and order, so re-quoting after an
 * intake edit keeps each person's health answers.
 *
 * @param {Object} draft - the wqQuoteDraft (script.js buildQuoteDraftFromInputs): dob, spouseDob,
 *   children:[{dob}], zipCode, primarySmoker ('true'/'false'), advisorFacts?
 * @param {Object} [previous] - facts from an earlier run
 * @param {Date} [asOf] - age reference date (the effective date when known)
 */
export function buildFactsFromDraft(draft, previous, asOf) {
  const d = draft && typeof draft === 'object' ? draft : {};
  const prev = sanitizeFacts(previous || d.advisorFacts || {});
  const people = [{ role: 'primary', age: ageFromDob(d.dob, asOf) }];
  if (d.spouseDob) people.push({ role: 'spouse', age: ageFromDob(d.spouseDob, asOf) });
  (Array.isArray(d.children) ? d.children : []).forEach((c) => {
    people.push({ role: 'child', age: ageFromDob(c && c.dob, asOf) });
  });
  const byRole = { primary: [], spouse: [], child: [] };
  prev.members.forEach((m) => byRole[m.role].push(m));
  const members = people.map((p) => {
    const earlier = byRole[p.role].shift();
    return { ...(earlier || {}), role: p.role, age: p.age };
  });
  return sanitizeFacts({
    ...prev,
    members,
    zip: d.zipCode || d.zip || prev.zip,
    state: d.state || prev.state,
    tobacco: smoker(d.primarySmoker, prev.tobacco),
  });
}

export const AdvisorFacts = {
  HEALTH_LEVELS, USAGE_LEVELS, PRIORITIES, NETWORK_WANTS, ADD_ONS, CONDITIONS, MEMBER_ROLES,
  createFacts, sanitizeFacts, missingFacts, hasHighHealthNeed, householdHealth, buildFactsFromDraft,
};
